import { Component, createRef } from 'react';

import { debounceTime, filter } from 'rxjs/operators';
import Dropdown from '../../components/ui/dropdown/dropdown';
import { FeatureDetailsComponent } from '../../components/ui/feature-details/feature-details.component';
import FeaturesTilesComponent from '../../components/ui/features-tiles/features-tiles.component';
import { FileListItemComponent } from '../../components/ui/file-list/file-list-item.component';
import { MotiveComponent } from '../../components/ui/motive/motive.component';
import RangeSelector, { RangeHandlerTypes, RangeSelectorResult } from '../../components/ui/range-selector/range-selector';
import RatingDisplayComponent from '../../components/ui/rating-display/rating-display.component';
import { RelatedLinksComponent } from '../../components/ui/related-links/related-links.component';
import { IOC_TYPES } from '../../context/ioc.types';
import { lazyInject } from '../../context/module';
import { FileUpload } from '../../models/file-upload';
import { Logger } from '../../models/logger';
import { UploadInProgressFile } from '../../models/upload-in-progress-file';
import { EnvironmentService } from '../../services/environment.service';
import { FileManagerService } from '../../services/file-manager.service';
import { FileProcessService } from '../../services/file-process.service';
import { FileUploadService } from '../../services/file-upload.service';
import { LightHttp } from '../../services/light-http.service';
import { MotivesService } from '../../services/motives.service';
import { SettingsService } from '../../services/settings.service';
import { SpinnerService } from '../../services/spinner.service';
import { UIContext } from '../../services/ui-context';
import { UploadedFilesService } from '../../services/uploaded-files.service';
import { WebSocketService } from '../../services/websocket.service';
import { WindowService } from '../../services/window.service';
import { ConvertFromVideo } from '../../types/capability.types';
import { FileSelectEvent } from '../../types/file-upload.types';
import { PageData, RouteProps, SeoFeature } from '../../types/route-types';
import { FromTypes, ProcessStageSubStateTypes, ProcessStageTypes, ProcessTypes, ProcessorMetadata, ToTypes } from '../../types/types';
import { BrowserUtils } from '../../ui-utils/browser-utils';
import { DateTimeUtils } from '../../ui-utils/date-time.utils';
import { FileUploadUtils } from '../../utils/file-upoad.utils';
import { FilenameUtils } from '../../utils/filename-utils';
import { cloneDeep, get, includes, isArray, isNaN, isNumber, toNumber } from '../../utils/lodash-min';
import { MotiveConfig, MotiveLocationTypes } from '../../utils/request.utils';
import { RouteUtils } from '../../utils/route.utils';
import { untilDestroyed } from '../../utils/until-destroyed';
import { FileUploadManager, SupportedFileTypes } from '../converter/file-upload-manager';
import FileUploadComponent from '../converter/file-upload/file-upload.component';
import Tooltip from '../ui/tooltip/tooltip.component';
import styles from './av-cutter.component.module.scss';
import { ProcessorMetadataUtils } from '../../utils/processor-metadata.utils';

const PLAYABLE_FILE_MIMI_TYPES = [
    'video/mp4', 'video/webm', 'audio/webm',
    'audio/mpeg', 'audio/MPA', 'audio/mpa-robust',
    'audio/vnd.wave', 'audio/wav', 'audio/wave', 'audio/x-wa'
];


interface AVCutterState {
    fileSelected: boolean;
    fakeUploadInProgressFileForSelectedFiles: UploadInProgressFile<SupportedFileTypes>[];
    fileWithMetadataList: UploadInProgressFile<SupportedFileTypes>[];
}

export default class AVCutterComponent extends Component<RouteProps, AVCutterState> {
    private readonly logger = Logger.getLogger('AVCutterComponent');

    get environment() {
        return this.envService.env;
    }
    get domainname() {
        return this.environment.domainname;
    }
    get sitename() {
        return this.environment.sitename;
    }

    get showRating() {
        return this.environment.sitename === this.environment.constants.xconvertId;
    }

    get showInfoGrid() {
        return this.environment.sitename === this.environment.constants.xconvertId;
    }

    // public sitename = 'xconvert';
    public title: string;
    public description: string;
    public features: Array<SeoFeature>;
    public pageData: PageData;

    public showHTML = false;
    public isMobile: boolean;
    public sourceFileUploadArray: FileUpload[] = [];

    public feature;

    public markdownData;
    featureDescriptionsDisabled = false;

    sidePanelFileUploads: FileUpload[];
    showSidePanelTriggered = false;
    canShowLivePreview = false;
    showLivePreview = false;

    effectiveMotives: MotiveConfig;
    // extensionSeoDataList: ExtensionSEOData[] = [];

    get internalAdsEnabled() {
        return this.effectiveMotives && this.effectiveMotives.internalMotivesEnabled;
    }
    get leftAdShown() {
        return this.effectiveMotives && this.effectiveMotives.convertPageLeft && !this.internalAdsEnabled;
    }
    get rightAdShown() {
        return this.effectiveMotives && this.effectiveMotives.convertPageRight && !this.internalAdsEnabled;
    }

    initialized = false;

    trimmedFileUploadArray: FileUpload[] = [];
    processType = 'convert';
    fileUrl: string;
    fileObjectURL: string;

    videoStart = 0;
    videoEnd: number;
    videoDuration: number;

    currentRange: RangeSelectorResult;

    selectedToType = 'same';
    supportedAVTypes: any[] = []; // TODO fix missing PSSelectItem type for dropdowns

    outputVideoRef = createRef<HTMLVideoElement>();

    get shouldShowLoading() {
        return this.state.fakeUploadInProgressFileForSelectedFiles.length;
    }

    get getLoadingFileList() {
        return Array.from(this.state.fakeUploadInProgressFileForSelectedFiles);
    }


    get outputVideoElement(): HTMLVideoElement {
        return this.outputVideoRef && this.outputVideoRef.current;
    }
    get outputVideoCurrentTime(): number {
        return this.outputVideoRef && this.outputVideoRef.current && this.outputVideoRef.current.currentTime || 0;
    }

    get isFileTrimInProgress(): boolean {
        return this.trimmedFileUploadArray && this.trimmedFileUploadArray.length > 0 && this.trimmedFileUploadArray.some(f => !f.isCompleted());
    }

    // @lazyInject(IOC_TYPES.>) private el: ElementRef<HTMLElement>;

    @lazyInject(IOC_TYPES.WebSocketService) private websocketService: WebSocketService;
    @lazyInject(IOC_TYPES.UIContext) private uiContext: UIContext;
    @lazyInject(IOC_TYPES.SpinnerService) private spinnerService: SpinnerService;
    @lazyInject(IOC_TYPES.FileProcessService) private fileProcessService: FileProcessService;
    @lazyInject(IOC_TYPES.UploadedFilesService) private uploadedFilesService: UploadedFilesService;
    @lazyInject(IOC_TYPES.WindowService) public windowService: WindowService;
    @lazyInject(IOC_TYPES.MotivesService) private motivesService: MotivesService;
    @lazyInject(IOC_TYPES.SettingsService) private settingsService: SettingsService;
    @lazyInject(IOC_TYPES.EnvironmentService) private envService: EnvironmentService;
    @lazyInject(IOC_TYPES.FileUploadService) private fileUploadService: FileUploadService;
    @lazyInject(IOC_TYPES.FileManagerService) private fileManagerService: FileManagerService;
    @lazyInject(IOC_TYPES.LightHttp) private lightHttp: LightHttp;

    readonly state: Readonly<AVCutterState> = {
        fileSelected: false,
        fakeUploadInProgressFileForSelectedFiles: [],
        fileWithMetadataList: [],
    };
    constructor(props, ref) {
        super(props);
        this.loadComponentData(this.props);
        this.updatePageData(props.pageData);
        this.updateSupportedAVTypes(props.pageData);
    }

    loadComponentData(props) {
        this.feature = RouteUtils.getPathFromProps(props);
        this.isMobile = BrowserUtils.isMobile();
    }

    componentDidMount() {
        this.fileManagerService.fileUploadChanged.pipe(
            untilDestroyed(this),
            debounceTime(100)
        ).subscribe({
            next: (fileUpload) => {
                if (fileUpload.result) {
                    const websocketResponse = fileUpload.ws;
                    if (websocketResponse && websocketResponse.processStage === ProcessStageTypes.COMPLETED && ProcessStageSubStateTypes.COMPLETED) {
                        this.updateVideoSource(fileUpload);
                    }
                }
                this.forceUpdate();
            }
        });

        const componentMountedTime = Date.now();
        this.fileUploadService.onFileSelected.pipe(
            filter(evt => evt.dispatchedAt.getTime() > componentMountedTime)
        ).subscribe({
            next: (data) => {
                if (!this.state.fileSelected) {
                    this.setState({
                        fileSelected: true,
                    });
                }
            }
        });
        this.isMobile = BrowserUtils.isMobile();
        this.feature = RouteUtils.getPathFromProps(this.props);
        // if (isPlatformBrowser(this.platformId)) {
        this.motivesService.onEffectiveMotiveSettingsChange
            .pipe(untilDestroyed(this))
            .subscribe(
                (motives) => this.effectiveMotives = motives,
                (error) => console.error(error),
            );
        this.settingsService.userSettings
            .pipe(untilDestroyed(this))
            .subscribe(
                (userSettings) => {
                    this.showLivePreview = get(userSettings, 'featureFlags.showLivePreview', false);
                    this.featureDescriptionsDisabled = get(userSettings, 'featureDescriptionsDisabled', false);
                },
            );

    }

    componentWillUnmount() {
        // for untilDestroyed
    }

    playOrPauseOutputVideo() {
        if (this.outputVideoElement.paused) {
            this.outputVideoElement.play();
            const seekToBigginingFn = () => {
                if (this.outputVideoElement.currentTime < this.currentRange.start || this.outputVideoElement.currentTime > this.currentRange.end) {
                    this.seekOutputVideoToStartOfTheCut();
                }
                if (!this.outputVideoElement.paused) {
                    setTimeout(() => seekToBigginingFn(), 10);
                }
            };
            seekToBigginingFn();
        } else {
            this.outputVideoElement.pause();
        }
    }

    seekOutputVideoToStartOfTheCut() {
        this.outputVideoElement.currentTime = this.currentRange.start;
        this.forceUpdate();
    }

    seekOutputVideo(seconds: number) {
        if (seconds < 0) {
            if (this.outputVideoElement.currentTime + seconds < this.currentRange.start) {
                this.outputVideoElement.currentTime = this.currentRange.start;
            } else {
                this.outputVideoElement.currentTime = this.outputVideoElement.currentTime + seconds;
            }
        } else if (seconds > 0) {
            if (this.outputVideoElement.currentTime + seconds > this.currentRange.end) {
                this.outputVideoElement.currentTime = this.currentRange.end;
            } else {
                this.outputVideoElement.currentTime = this.outputVideoElement.currentTime + seconds;
            }
        }
        this.forceUpdate();
    }

    public updateOutputVideoStartAndEnd(rangeValue: RangeSelectorResult) {
        this.currentRange = rangeValue;
        this.outputVideoElement.currentTime = rangeValue.handlerType === RangeHandlerTypes.START ? rangeValue.start : rangeValue.end;
        this.forceUpdate();
    }

    async fileSelected(event: FileSelectEvent) {
        await this.spinnerService.runWithLoader(async () => {
            this.fileUrl = null;
            const URL = window.URL || window.webkitURL;
            URL.revokeObjectURL(this.fileObjectURL);
            this.fileObjectURL = null;

            const fileUploadManager = new FileUploadManager(
                this.state.fakeUploadInProgressFileForSelectedFiles,
                this.state.fileWithMetadataList,
                this.props,
                this.logger,
                [ConvertFromVideo.TYPE],
                ToTypes.VIDEO,
                () => { this.initialized = true; },
                () => { },
            );
            await fileUploadManager.fileSelected(event);
            const options = [
                { formName: 'outputExtension', formValue: this.pageData.fromType.indexOf(FromTypes.AUDIO) > -1 ? '.mp3' : '.mp4' },
            ];
            if (this.pageData.fromType.indexOf(FromTypes.VIDEO) > -1) {
                options.push({ formName: 'c:v', formValue: 'libx264' });
            }
            const fileUpload = this.convertNoneMergeUploadInProgressFilesToFileUploads(options)[0];
            this.processFile(fileUpload, () => {
                this.forceUpdate();
            }, true);
            this.forceUpdate();
        }).catch(() => {
            this.forceUpdate();
        });
    }

    private updateVideoSource(fileUpload: FileUpload) {
        if (!this.fileObjectURL) {
            const a = fileUpload.metadata.processorMetadataList[0];
            this.fileUrl = ProcessorMetadataUtils.getUploadedFileDownloadEndpoint(a) +
                '/range/' + a.fileMetadata.safeName + `?content_type=${FilenameUtils.getFileMimeTypeFromFilename(a.fileMetadata.safeName)}`;
            const file = isArray(fileUpload.file) ? fileUpload.file[0] : fileUpload.file;
            const URL = window.URL || window.webkitURL;
            this.fileObjectURL = URL.createObjectURL(file);
            this.forceUpdate();
        }
    }

    videoLoaded() {
        this.videoEnd = this.outputVideoElement.duration;
        this.videoStart = 0;
        this.videoDuration = this.outputVideoElement.duration;
        this.updateOutputVideoStartAndEnd({ start: this.videoStart, end: this.videoEnd, handlerType: RangeHandlerTypes.START });
        this.forceUpdate();
    }

    convertNoneMergeUploadInProgressFilesToFileUploads(options): FileUpload[] {
        return this.state.fileWithMetadataList.map(fmd => {
            return this.convertNoneMergeUploadInProgressFilesToFileUpload(fmd, options);
        });
    }

    updateToVideoType(selectedItem: any) { // TODO fix missing PSSelectItem
        this.selectedToType = selectedItem.item;
    }

    convertNoneMergeUploadInProgressFilesToFileUpload(fmd: UploadInProgressFile<SupportedFileTypes>, extraOptions?: any[]): FileUpload {
        const options: any = [
            { formName: 'frag_duration', formValue: '100' },
            { formName: 'movflags', formValue: 'faststart' },
            // { formName: 'preset', formValue: 'veryfast' },
            // { formName: 'outputScale', formValue: 'original' },
        ];

        options.push({
            formName: 'outputQualityAndSize',
            formValue: [
                { formName: 'percentage', formValue: '100' },
                { formName: 'pass', formValue: 'nopass' }
            ]
        });

        if (extraOptions) {
            options.push(...extraOptions);
        }
        const orientationAngleList: string[] = [(fmd.getOrientation() && fmd.getOrientation().angle) || '0'];
        const orientationFlipList: string[] = [(fmd.getOrientation() && fmd.getOrientation().isFliped) || 'false'];

        const fileUpload = FileUploadUtils.toFileUpload(
            fmd,
            this.websocketService.getId(),
            ProcessTypes.CONVERT,
            [ConvertFromVideo.TYPE],
            ToTypes.VIDEO,
            options,
            null,
            fmd.uniqueProcessType,
            this.props.pageData,
        );

        fileUpload.metadataList = [cloneDeep(fileUpload.metadata)];
        return fileUpload;
    }

    async trimAndDownload() {
        if (this.isFileTrimInProgress) {
            this.uiContext.showErrorDialog({
                title: 'Error', text: 'Please wait until current process to be finished'
            });
            return;
        }
        const fileUpload = this.sourceFileUploadArray[0];
        const fmds = await FileUploadUtils.toUploadInProgressFile<SupportedFileTypes>(fileUpload);
        const fmd: UploadInProgressFile<SupportedFileTypes> = fmds[0];
        const extraOptions: any = [
            {
                formName: 'outputTrim',
                formValue: [
                    {
                        formName: 'START_TIME',
                        formValue: `${this.currentRange.start}`,
                        formValueType: 'seconds'
                    },
                    {
                        formName: 'DURATION',
                        formValue: `${this.currentRange.end - this.currentRange.start}`,
                        formValueType: 'seconds'
                    }
                ]
            },
        ];
        const extension = '.' + FilenameUtils.getExtension((this.sourceFileUploadArray[0].file as any).name);
        if (this.selectedToType === 'same') {
            extraOptions.push({ formName: 'outputExtension', formValue: extension });
            // Seek doesn't work property when codec is copy
            // extraOptions.push({ formName: 'c:v', formValue: 'copy' });
            // extraOptions.push({ formName: 'c:a', formValue: 'copy' });
        } else {
            if (extension.toLocaleLowerCase().endsWith(this.selectedToType)) {
                // Seek doesn't work property when codec is copy
                // extraOptions.push({ formName: 'c:v', formValue: 'copy' });
                // extraOptions.push({ formName: 'c:a', formValue: 'copy' });
            }
            extraOptions.push({ formName: 'outputExtension', formValue: this.selectedToType });
        }
        const trimmedFileUpload = this.convertNoneMergeUploadInProgressFilesToFileUpload(fmd, extraOptions);
        this.trimmedFileUploadArray.splice(0, this.trimmedFileUploadArray.length);
        await this.processFile(trimmedFileUpload, () => {
            this.trimmedFileUploadArray.unshift(trimmedFileUpload);
            this.forceUpdate();
        });

    }

    private async processFile(fileUpload: FileUpload, postProcessFn: () => void, skipIfPlayable = false) {
        if (skipIfPlayable && fileUpload) {
            if (!isArray(fileUpload.file)) {
                const file: any = fileUpload.file;
                if (includes(PLAYABLE_FILE_MIMI_TYPES, file.type)) {
                    this.sourceFileUploadArray.splice(0, this.sourceFileUploadArray.length, fileUpload);
                    this.updateVideoSource(fileUpload);
                    postProcessFn();
                    return;
                }
            }
        }
        try {
            const instance = await this.spinnerService.show(`Submitted files. Submitting file ${fileUpload.name} to process...`);
            this.sourceFileUploadArray.splice(0, this.sourceFileUploadArray.length, fileUpload);
            this.fileManagerService.addFileUpload(fileUpload);
            this.fileProcessService.processFile(fileUpload).subscribe({
                next: (resp) => {
                },
                error: (err) => {
                    this.logger.error({
                        message: 'AV cut process failed.',
                        error: err,
                        data: FileUpload.toLoggableObject(fileUpload),
                    });
                    this.uiContext.showErrorDialog({ title: 'Error', text: `Failed to ${this.processType} the file. Please try using XConvert on Chrome incognito mode.` });
                    this.spinnerService.hide(instance);
                },
                complete: () => {
                    this.spinnerService.hide(instance);
                    postProcessFn();
                }
            });
            this.spinnerService.updateMessage(instance, `Successfully submitted ${fileUpload.name} for processing...`);
        } catch (e) {
            // suppress
        } finally {

        }
    }

    updateCurrentTime(ev) {
        const value = toNumber(ev.target.value);
        if (isNumber(value) && !isNaN(value)) {
            this.outputVideoElement.currentTime = value;
        }
    }

    updateRangeSliderStartToCurrentTime() {
        this.videoStart = this.outputVideoCurrentTime;
        this.currentRange.start = this.outputVideoCurrentTime;
        this.forceUpdate();
    }

    updateRangeSliderEndToCurrentTime() {
        this.videoEnd = this.outputVideoCurrentTime;
        this.currentRange.end = this.outputVideoCurrentTime;
        this.forceUpdate();
    }

    toggle() {
        this.videoStart = this.outputVideoCurrentTime;
    }

    valueFormatter(value: string) {
        return value; // TODO Preact fix new FormatSecondsToMediaDurationPipe().transform(value);
    }

    private updateSupportedAVTypes(pageData: PageData) {
        const supportedVideoTypes = [
            {
                id: 'same',
                text: 'Same As Input',
                item: 'same',
                selected: true,
            },
            {
                id: 'mp4',
                text: 'MP4',
                item: '.mp4',
                selected: false,
            },
            {
                id: 'mkv',
                text: 'MKV',
                item: '.mkv',
                selected: false,
            },
            {
                id: 'mov',
                text: 'MOV',
                item: '.mov',
                selected: false,
            },
            {
                id: 'webm',
                text: 'WebM',
                item: '.webm',
                selected: false,
            },
            {
                id: 'mts',
                text: 'MTS',
                item: '.mts',
                selected: false,
            },
            {
                id: 'avi',
                text: 'AVI',
                item: '.avi',
                selected: false,
            },
        ];

        const supportedAudioTypes = [
            {
                id: 'same',
                text: 'Same As Input',
                item: 'same',
                selected: true,
            },
            {
                id: 'mp3',
                text: 'MP3',
                item: '.mp3',
                selected: false,
            },
            {
                id: 'flac',
                text: 'FLAC',
                item: '.flac',
                selected: false,
            },
            {
                id: 'wav',
                text: 'WAV',
                item: '.wav',
                selected: false,
            },
            {
                id: 'm4a',
                text: 'M4A',
                item: '.m4a',
                selected: false,
            },
            {
                id: 'opus',
                text: 'OPUS',
                item: '.opus',
                selected: false,
            },
            {
                id: 'm4r',
                text: 'M4R',
                item: '.m4r',
                selected: false,
            },
        ];

        if (pageData.fromType.indexOf(FromTypes.AUDIO) > -1) {
            this.supportedAVTypes = supportedAudioTypes;
        } else {
            this.supportedAVTypes = supportedVideoTypes;
        }
    }

    private updatePageData(value: PageData) {
        this.pageData = value;
        this.features = value.page_seo.feature;
        this.title = value.page_seo.title;
        this.description = value.page_seo.description && value.page_seo.description.replace(/\s+/g, ' ');
        this.uploadedFilesService.initializeForCompatibility(value.fromType, value.accept);
        this.uploadedFilesService.notyfyAll();
    }

    getVideoAndControllers() {
        return <section>
            <Tooltip text="Start new">
                <button className={`x-btn x-btn-primary ${styles['start-new-btn']} mb-2`} onClick={e => this.startNew()}>
                    + Start New
                </button>
            </Tooltip>
            <div className={styles['video-container']}>
                {
                    !this.fileUrl && this.sourceFileUploadArray && this.sourceFileUploadArray.length > 0 && <div>
                        <FileListItemComponent
                            {...this.props}
                            fileUpload={this.sourceFileUploadArray[0]}>
                        </FileListItemComponent>
                        {/* <file-list-item [fileUpload]="sourceFileUploadArray[0]" [hideFooterButtons]="true"></file-list-item> */}
                    </div>
                }
                {
                    false && !this.fileUrl && (!this.sourceFileUploadArray || this.sourceFileUploadArray.length === 0) && <div >
                        <h3 className="text-center mb-3 mt-2">Please select a file to see the preview.</h3>
                    </div>
                }
                {
                    this.fileUrl && <video ref={this.outputVideoRef} controls
                        onTimeUpdate={() => this.forceUpdate()}
                        onLoadedMetadata={e => this.videoLoaded()}>
                        <source src={this.fileObjectURL} />
                        <source src={this.fileUrl} />
                    </video>
                }
            </div>
            <div className="mb-2">
                <label>
                    <b>Current Timestamp:</b>
                </label>
                <div>
                    {isNumber(this.outputVideoCurrentTime) ? DateTimeUtils.secondsToTime(this.outputVideoCurrentTime, true) : ''}
                </div>
            </div>
            <div className="mb-2">
                <label htmlFor="outputStartTime">
                    <b>Current Time (seconds):</b>
                </label>
                <div>
                    <input type="number" className="form-control form-control-sm" placeholder="Current video time"
                        aria-label="Current video time" aria-describedby="btnGroupAddon"
                        onChange={$event => this.updateCurrentTime($event)}
                        onKeyUp={$event => this.updateCurrentTime($event)}
                        value={this.outputVideoCurrentTime} step="0.01" />
                </div>
            </div>
            <div className={styles['apply-current-range-btns']}>
                <button type="button" className="x-btn x-btn-gray x-btn-gray-outline"
                    onClick={e => this.updateRangeSliderStartToCurrentTime()}>
                    Use as start
                </button>
                <button type="button" className="x-btn x-btn-gray x-btn-gray-outline"
                    onClick={e => this.updateRangeSliderEndToCurrentTime()}>
                    Use as end
                </button>
            </div>
        </section>;
    }

    private getSEOSections() {
        return <div className="">
            {
                <div className="mt-3" >
                    <RelatedLinksComponent relatedLinks={this.props.relatedLinks}></RelatedLinksComponent>
                </div>
            }
            {
                <div className="mt-3" >
                    <FeatureDetailsComponent markdown={this.props.markdown}></FeatureDetailsComponent>
                </div>
            }
            {
                this.showRating && <div className="text-center mt-3">
                    <RatingDisplayComponent
                        rating={this.props.rating}
                        title={this.title}
                        feature={this.feature}
                        description={this.description}>
                    </RatingDisplayComponent>
                </div>
            }
            {
                this.showInfoGrid && !this.featureDescriptionsDisabled && <div className="mt-3">
                    {
                        this.features && <FeaturesTilesComponent features={this.features}></FeaturesTilesComponent>
                    }
                </div>
            }
        </div>;
    }

    startNew() {
        // this.setState({
        //     fileSelected: false,
        // });
        // This fixes the bug where seekbar shows the previously uploaded videos duration
        window.location.reload();
    }

    render() {
        if (!this.state.fileSelected) {
            return <div key="no-file" className={`cutter-container ${styles['cutter-container']}`}>
                {
                    <div className={`page-heading text-center ${styles['page-heading']}`}>
                        <div className={styles.title}><b>{this.title}</b></div>
                        <div className={`${styles.description} ${styles['secondary-text']}`}>{this.description}</div>
                    </div>
                }
                <div className={styles['file-upload-btns']}>
                    <div className='row'>
                        <div className='col-12 col-md-4 col-lg-4'></div>
                        <div className='col-12 col-md-4 col-lg-4'>
                            <FileUploadComponent fileSelected={$event => this.fileSelected($event)}
                                singleFileSelect={true}
                                fileSelectButtonClicked={() => console.log('fileSelectButtonClicked from dom')} acceptedFileTypes={this.pageData?.accept}
                                uploadBtnText={this.sourceFileUploadArray.length === 0 ? 'Select File' : 'Select Another File'}
                                uploadBtnStyles={{ 'minWidth': 'unset', 'width': '100%' }}
                                uploadBtnClassName='btn-primary'>
                            </FileUploadComponent>
                        </div>
                        <div className='col-12 col-md-4 col-lg-4'></div>
                    </div>
                </div>
                {
                    this.getSEOSections()
                }

            </div>
        }

        return <div key="has-file" className={`cutter-container ${styles['cutter-container']}`}>
            <div className="gcontent-container">
                {
                    <MotiveComponent
                        data="9534307497" containerWidth="100%" containerHeight="60px"
                        location={MotiveLocationTypes.convertPageBelowFeatureTitle} adWidth="720px" adHeight="60px" showAds={true}
                        className='width_major_md'
                    ></MotiveComponent>
                }
            </div>
            <div className={`${styles.editor} `}>
                <div className={`${styles['video-and-stat-container']}`}>
                    {
                        this.getVideoAndControllers()

                    }
                </div>

                <div className={`${styles['tool-container']}`}>
                    <section>
                        <h4 className='d-none-sm'>Trim or cut options</h4>
                        <p className='d-none-sm'>
                            Use the sliders below to trim and/or cut your media to the right length. If you select the
                            <strong>Current Time (seconds)</strong>,
                            you can also use the arrow keys for finer adjustment in milliseconds.
                        </p>

                        <div className="btn-toolbar mb-3" role="toolbar" aria-label="Set start time and end time">
                            <div className={styles['video-seek-buttons']} role="group" aria-label="Set buttons">
                                <button disabled={!this.fileUrl} className="x-btn x-btn-gray x-btn-gray-outline x-btn-svg" onClick={e => this.playOrPauseOutputVideo()}>
                                    <img src={this.outputVideoElement?.paused ? "/icons/svg/play.svg" : "/icons/svg/pause.svg"} />
                                </button>

                                <button disabled={!this.fileUrl} className="x-btn x-btn-gray x-btn-gray-outline x-btn-svg" onClick={e => this.seekOutputVideoToStartOfTheCut()}>
                                    <img src="/icons/svg/arrow-counterclockwise.svg" />
                                </button>
                                <button disabled={!this.fileUrl} className="x-btn x-btn-gray x-btn-gray-outline text-flex-center" onClick={e => this.seekOutputVideo(-5)}>
                                    -5s
                                </button>
                                <button disabled={!this.fileUrl} className="x-btn x-btn-gray x-btn-gray-outline text-flex-center" onClick={e => this.seekOutputVideo(5)}>
                                    +5s
                                </button>
                            </div>
                        </div>
                        <div className="mb-3">
                            {
                                this.videoDuration && <RangeSelector
                                    valueFormatter={(e) => this.valueFormatter(e)}
                                    max={this.videoDuration}
                                    start={this.currentRange?.start || this.videoStart}
                                    end={this.currentRange?.end || this.videoEnd}
                                    step={0.01}
                                    onChange={$event => this.updateOutputVideoStartAndEnd($event)}>
                                </RangeSelector>
                            }
                        </div>

                        <div className="mb-3">
                            <div className="mb-2">
                                <label htmlFor="outputStartTime">Start</label>
                                <input type="text" id="outputStartTime"
                                    placeholder="Current video time" aria-label="Current video time"
                                    aria-describedby="btnGroupAddon" readOnly
                                    value={this.currentRange?.start} />
                                {/*TODO Pract fix value formatter above | formatSecondsToMediaDuration */}
                            </div>
                            <div className="mb-2">
                                <label htmlFor="outputEndTime">End</label>
                                <input type="text" id="outputEndTime"
                                    placeholder="Current video time" aria-label="Current video time"
                                    aria-describedby="btnGroupAddon" readOnly
                                    value={this.currentRange?.end} />
                                {/*TODO Pract fix value formatter above | formatSecondsToMediaDuration */}
                            </div>
                        </div>
                    </section>
                    <section>
                        <div className={styles['save-result']}>
                            <Dropdown
                                // btnClass="btn btn-secondary"
                                label="Output Format"
                                initialValue={this.supportedAVTypes.find(a => a.selected)}
                                values={this.supportedAVTypes}
                                onSelect={($event) => this.updateToVideoType($event)}
                                valueFormatter={(v: any) => v && v.text}
                                hasTooltip={(v) => false}
                                getTooltip={(v) => null}
                            ></Dropdown>
                            <button disabled={!this.fileUrl} className="x-btn x-button-primary" onClick={e => this.trimAndDownload()}>
                                Trim
                            </button>
                        </div>
                        <div className={styles['saved-files']}>
                            {
                                this.trimmedFileUploadArray.map(fileUpload => {
                                    return <FileListItemComponent key={`av-cutter-list-item-${fileUpload.id}`}
                                        {...this.props}
                                        fileUpload={fileUpload}
                                        reeditButtonEnabled={false}
                                        deleteButtonEnabled={false}
                                    >
                                    </FileListItemComponent>
                                })
                            }
                        </div>
                    </section>
                    <section>
                        <div>
                            <div>
                                {
                                    <div className="gcontent-container">
                                        {
                                            <MotiveComponent
                                                data="9534307497" containerWidth="100%" containerHeight="60px"
                                                location={MotiveLocationTypes.convertPageBelowFeatureTitle} adWidth="720px" adHeight="60px" showAds={true}
                                                className='width_major_md'></MotiveComponent>
                                        }
                                    </div>
                                }
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    }
}
